import React, { useState, useRef } from 'react';
import {
    Upload, FileSpreadsheet, Download, CheckCircle2, AlertCircle,
    Loader2, X, ArrowRight, Eye, RefreshCw
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { parseProspectsFile, downloadProspectsTemplate } from '../utils/excelProspects';
import { bulkCreateProspects } from '../services/prospectService';
import { STATUT_MAP, SOURCE_MAP, interetLevel } from '../config/prospects';

const ProspectImportModal = ({ isOpen, onClose, onImportSuccess }) => {
    const fileInputRef = useRef(null);
    const [file, setFile] = useState(null);
    const [parsing, setParsing] = useState(false);
    const [parsedData, setParsedData] = useState(null);
    const [error, setError] = useState('');
    const [importing, setImporting] = useState(false);
    const [isDragOver, setIsDragOver] = useState(false);

    if (!isOpen) return null;

    const handleFileProcess = async (selectedFile) => {
        if (!selectedFile) return;
        const validExts = ['.xlsx', '.xls', '.csv'];
        const isMatch = validExts.some(ext => selectedFile.name.toLowerCase().endsWith(ext));
        if (!isMatch) {
            setError('Format non supporté. Veuillez choisir un fichier Excel (.xlsx, .xls) ou CSV (.csv).');
            return;
        }

        setError('');
        setFile(selectedFile);
        setParsing(true);
        try {
            const res = await parseProspectsFile(selectedFile);
            if (res.prospects.length === 0) {
                setError('Aucun prospect valide trouvé dans le fichier.');
                setParsedData(null);
            } else {
                setParsedData(res);
            }
        } catch (err) {
            console.error('Parsing error:', err);
            setError(err.message || 'Erreur lors de la lecture du fichier Excel.');
            setParsedData(null);
        } finally {
            setParsing(false);
        }
    };

    const handleDrop = (e) => {
        e.preventDefault();
        setIsDragOver(false);
        if (e.dataTransfer.files && e.dataTransfer.files[0]) {
            handleFileProcess(e.dataTransfer.files[0]);
        }
    };

    const handleImportConfirm = async () => {
        if (!parsedData || parsedData.prospects.length === 0) return;
        setImporting(true);
        setError('');
        try {
            const res = await bulkCreateProspects(parsedData.prospects);
            onImportSuccess?.(res);
            handleClose();
        } catch (err) {
            setError(err.response?.data?.message || "Erreur lors de l'enregistrement des prospects.");
        } finally {
            setImporting(false);
        }
    };

    const handleClose = () => {
        setFile(null);
        setParsedData(null);
        setError('');
        setImporting(false);
        onClose();
    };

    return (
        <AnimatePresence>
            <div className="fixed inset-0 z-[150] flex items-center justify-center p-4">
                {/* Backdrop */}
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    onClick={handleClose}
                    className="fixed inset-0 bg-slate-900/50 backdrop-blur-xs"
                />

                {/* Modal Container */}
                <motion.div
                    initial={{ opacity: 0, scale: 0.95, y: 15 }}
                    animate={{ opacity: 1, scale: 1, y: 0 }}
                    exit={{ opacity: 0, scale: 0.95, y: 15 }}
                    className="bg-white rounded-2xl shadow-2xl border border-slate-200 w-full max-w-4xl max-h-[90vh] flex flex-col relative z-10 overflow-hidden"
                >
                    {/* Header */}
                    <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between bg-slate-50/50 shrink-0">
                        <div className="flex items-center gap-3">
                            <div className="p-2 bg-emerald-50 text-emerald-700 border border-emerald-200/60 rounded-xl">
                                <FileSpreadsheet size={20} />
                            </div>
                            <div>
                                <h3 className="text-base font-black text-slate-900 tracking-tight">Importer des prospects depuis Excel</h3>
                                <p className="text-[11px] font-medium text-slate-500">Formats supportés : .xlsx, .xls, .csv</p>
                            </div>
                        </div>
                        <div className="flex items-center gap-2">
                            <button
                                type="button"
                                onClick={downloadProspectsTemplate}
                                className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white hover:bg-slate-50 border border-slate-200 rounded-lg text-xs font-bold text-slate-700 transition shadow-2xs"
                                title="Télécharger le modèle avec les colonnes exactes"
                            >
                                <Download size={13} className="text-emerald-600" />
                                <span>Télécharger le modèle Excel</span>
                            </button>
                            <button
                                onClick={handleClose}
                                className="p-1.5 hover:bg-slate-100 rounded-lg transition text-slate-400 hover:text-slate-700"
                            >
                                <X size={18} />
                            </button>
                        </div>
                    </div>

                    {/* Content */}
                    <div className="p-6 overflow-y-auto flex-1 space-y-5">
                        {/* Drag and Drop Zone */}
                        <div
                            onDragOver={(e) => { e.preventDefault(); setIsDragOver(true); }}
                            onDragLeave={() => setIsDragOver(false)}
                            onDrop={handleDrop}
                            onClick={() => fileInputRef.current?.click()}
                            className={`border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-all ${
                                isDragOver
                                    ? 'border-emerald-500 bg-emerald-50/50 scale-[0.99]'
                                    : file
                                    ? 'border-emerald-200 bg-emerald-50/20'
                                    : 'border-slate-200 hover:border-slate-400 bg-slate-50/30'
                            }`}
                        >
                            <input
                                ref={fileInputRef}
                                type="file"
                                accept=".xlsx,.xls,.csv"
                                className="hidden"
                                onChange={(e) => e.target.files?.[0] && handleFileProcess(e.target.files[0])}
                            />

                            <div className="max-w-md mx-auto flex flex-col items-center">
                                <div className="w-12 h-12 rounded-full bg-emerald-100/70 text-emerald-600 flex items-center justify-center mb-3">
                                    {parsing ? <Loader2 size={24} className="animate-spin" /> : <Upload size={24} />}
                                </div>
                                <div className="text-sm font-bold text-slate-900 mb-1">
                                    {file ? file.name : 'Glissez-déposez votre fichier Excel ici'}
                                </div>
                                <p className="text-xs text-slate-500">
                                    ou <span className="text-emerald-600 font-bold hover:underline">cliquez pour parcourir</span> vos fichiers (.xlsx, .xls, .csv)
                                </p>
                            </div>
                        </div>

                        {/* Error Alert */}
                        {error && (
                            <div className="p-4 bg-red-50 border border-red-200 rounded-xl flex items-start gap-3 text-red-700 text-xs font-bold">
                                <AlertCircle size={16} className="shrink-0 mt-0.5 text-red-500" />
                                <div>{error}</div>
                            </div>
                        )}

                        {/* Preview of Parsed Data */}
                        {parsedData && (
                            <div className="space-y-3">
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-2 text-xs font-black text-slate-900 uppercase tracking-wider">
                                        <Eye size={14} className="text-emerald-600" />
                                        <span>Aperçu des données détectées</span>
                                        <span className="text-emerald-700 bg-emerald-50 border border-emerald-200 px-2 py-0.5 rounded-full font-mono text-[11px]">
                                            {parsedData.totalParsed} prospect(s) prêt(s) à être importé(s)
                                        </span>
                                    </div>
                                    <button
                                        type="button"
                                        onClick={() => fileInputRef.current?.click()}
                                        className="inline-flex items-center gap-1 text-[11px] font-bold text-slate-500 hover:text-slate-800"
                                    >
                                        <RefreshCw size={11} />
                                        Changer de fichier
                                    </button>
                                </div>

                                {parsedData.errors && parsedData.errors.length > 0 && (
                                    <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-amber-800 text-[11px] font-medium max-h-24 overflow-y-auto">
                                        <div className="font-bold mb-1">Avertissements ({parsedData.errors.length}) :</div>
                                        {parsedData.errors.map((err, i) => (
                                            <div key={i}>• {err}</div>
                                        ))}
                                    </div>
                                )}

                                {/* Preview Table */}
                                <div className="border border-slate-200 rounded-xl overflow-hidden max-h-64 overflow-y-auto bg-white">
                                    <table className="w-full text-left text-xs border-collapse">
                                        <thead className="bg-slate-50 border-b border-slate-200 text-[10px] font-black uppercase tracking-wider text-slate-500 sticky top-0">
                                            <tr>
                                                <th className="px-3 py-2">Établissement</th>
                                                <th className="px-3 py-2">Contact</th>
                                                <th className="px-3 py-2">Téléphone</th>
                                                <th className="px-3 py-2">Source</th>
                                                <th className="px-3 py-2">Statut</th>
                                                <th className="px-3 py-2">Intérêt</th>
                                                <th className="px-3 py-2">Prochaine action</th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-slate-100">
                                            {parsedData.prospects.slice(0, 50).map((row, idx) => {
                                                const st = STATUT_MAP[row.statut];
                                                const src = SOURCE_MAP[row.source];
                                                const lvl = interetLevel(row.interet);
                                                return (
                                                    <tr key={idx} className="hover:bg-slate-50/50">
                                                        <td className="px-3 py-2 font-bold text-slate-900">{row.etablissement}</td>
                                                        <td className="px-3 py-2 text-slate-600">{row.contact || '—'}</td>
                                                        <td className="px-3 py-2 font-mono text-slate-600">{row.telephone || '—'}</td>
                                                        <td className="px-3 py-2">
                                                            <span className="text-[10px] font-bold text-slate-600 bg-slate-100 px-1.5 py-0.5 rounded">
                                                                {src?.label || row.source}
                                                            </span>
                                                        </td>
                                                        <td className="px-3 py-2 whitespace-nowrap">
                                                            <span className="text-[10px] font-bold">
                                                                {st?.emoji} {st?.label || row.statut}
                                                            </span>
                                                        </td>
                                                        <td className="px-3 py-2">
                                                            <span className={`text-[10px] font-black font-mono ${lvl.text}`}>
                                                                {row.interet}%
                                                            </span>
                                                        </td>
                                                        <td className="px-3 py-2 text-blue-700 font-semibold">{row.prochaineAction || '—'}</td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                                {parsedData.prospects.length > 50 && (
                                    <p className="text-[10px] text-slate-400 text-right">
                                        Affichage des 50 premiers prospects sur {parsedData.prospects.length}...
                                    </p>
                                )}
                            </div>
                        )}
                    </div>

                    {/* Footer */}
                    <div className="px-6 py-4 border-t border-slate-100 bg-slate-50/50 flex items-center justify-between shrink-0">
                        <div className="text-xs text-slate-500">
                            {parsedData
                                ? `${parsedData.prospects.length} prospect(s) prêt(s)`
                                : 'Téléchargez le modèle si vous souhaitez préparer vos colonnes'}
                        </div>
                        <div className="flex items-center gap-2">
                            <button
                                type="button"
                                onClick={handleClose}
                                disabled={importing}
                                className="px-4 py-2 text-xs font-bold text-slate-600 hover:bg-slate-200/60 rounded-lg transition"
                            >
                                Annuler
                            </button>
                            <button
                                type="button"
                                onClick={handleImportConfirm}
                                disabled={!parsedData || parsedData.prospects.length === 0 || importing}
                                className="inline-flex items-center gap-2 px-5 py-2 bg-emerald-600 hover:bg-emerald-700 disabled:bg-slate-200 disabled:text-slate-400 text-white rounded-lg text-xs font-bold transition shadow-sm"
                            >
                                {importing ? (
                                    <>
                                        <Loader2 size={14} className="animate-spin" />
                                        <span>Importation en cours...</span>
                                    </>
                                ) : (
                                    <>
                                        <CheckCircle2 size={14} />
                                        <span>Importer {parsedData?.prospects?.length ? `${parsedData.prospects.length} prospect(s)` : ''}</span>
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                </motion.div>
            </div>
        </AnimatePresence>
    );
};

export default ProspectImportModal;
